/**
 * Слой совместимости с платформой.
 *
 * Приложение живёт в двух местах: внутри Telegram-мини-аппа и на сайте
 * webtransfer.app (в том числе установленное на телефон как PWA). Всё, что в
 * Telegram делает его SDK, в браузере надо делать самим — иначе половина
 * кнопок просто ничего не делает.
 *
 * Правило простое: компоненты зовут функции отсюда, а не WebApp напрямую.
 */

import WebApp from "@twa-dev/sdk";
import { getInitialLanguage } from "../constants/languages";

/**
 * Запущены ли мы внутри Telegram.
 *
 * Проверяем именно наличие initData: SDK подключается и в обычном браузере,
 * но подписанных данных там нет. По user-agent определять нельзя — Telegram
 * на Android присылает обычный Chrome.
 */
export const isTelegramMiniApp: boolean = Boolean(
  typeof window !== "undefined" && WebApp.initData && WebApp.initData.length > 0
);

/** Ссылка на бота — куда возвращаться из веба, чтобы открыть мини-апп. */
export const BOT_LINK = "https://t.me/Webtransfer_apps_bot";
export const MINI_APP_LINK = "https://t.me/Webtransfer_apps_bot/webtransfer";

/** Открыть внешнюю ссылку. */
export function openExternalLink(url: string): void {
  if (isTelegramMiniApp) {
    WebApp.openLink(url);
    return;
  }
  // noopener обязателен: без него открытая вкладка получает доступ к window.opener.
  window.open(url, "_blank", "noopener,noreferrer");
}

/** Открыть ссылку внутри Telegram (t.me/...). */
export function openTelegramLink(url: string): void {
  if (isTelegramMiniApp) {
    WebApp.openTelegramLink(url);
    return;
  }
  // В браузере t.me сам предложит открыть приложение Telegram.
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Поделиться заранее подготовленным сообщением.
 *
 * В Telegram это нативный выбор чата (shareMessage). В браузере такого нет,
 * поэтому используем системное «Поделиться», а если и его нет — копируем
 * ссылку в буфер. Возвращаем, удалось ли: вызывающий код показывает результат.
 */
export async function shareMessage(
  messageId: string,
  fallback: { url: string; title?: string; text?: string }
): Promise<boolean> {
  if (isTelegramMiniApp) {
    return new Promise((resolve) => {
      try {
        WebApp.shareMessage(messageId, (success: boolean) => resolve(success));
      } catch {
        resolve(false);
      }
    });
  }

  const shareData = {
    title: fallback.title,
    text: fallback.text,
    url: fallback.url,
  };

  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share(shareData);
      return true;
    } catch {
      // Пользователь закрыл системное окно — это не ошибка, но и не успех.
      return false;
    }
  }

  try {
    await navigator.clipboard.writeText(fallback.url);
    return true;
  } catch {
    return false;
  }
}

/** Инициализация окна мини-аппа. В браузере ничего этого не существует. */
export function initPlatformChrome(): void {
  if (!isTelegramMiniApp) {
    return;
  }
  WebApp.ready();
  WebApp.expand();
  WebApp.disableVerticalSwipes();
  WebApp.setBackgroundColor("#fff");
}

/** Реферальный код: в Telegram — start_param, в вебе — ?ref= в адресе. */
export function getReferralCode(): string {
  if (isTelegramMiniApp) {
    return (WebApp.initDataUnsafe.start_param as string) || "";
  }

  if (typeof window === "undefined") {
    return "";
  }

  const fromUrl = new URLSearchParams(window.location.search).get("ref");
  if (fromUrl) {
    // Переживает переход на вход через Telegram или Google и возврат обратно.
    try {
      localStorage.setItem("referral_code", fromUrl);
    } catch {
      /* приватный режим — обойдёмся без запоминания */
    }
    return fromUrl;
  }

  try {
    return localStorage.getItem("referral_code") || "";
  } catch {
    return "";
  }
}

/** Язык интерфейса по умолчанию: из настроек, Telegram или настроек браузера. */
export function getPlatformLanguage(): string {
  const tgLang = isTelegramMiniApp ? (WebApp.initDataUnsafe.user?.language_code as string) : undefined;
  return getInitialLanguage(tgLang);
}

/** Открыто ли приложение как установленное на телефон (PWA). */
export function isStandalonePwa(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS до сих пор сообщает об этом своим нестандартным способом.
    (window.navigator as any).standalone === true
  );
}
